import { Group } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import {
  accountBadgeClassName,
  accountOutlineButtonClassName,
  accountPrimaryButtonClassName,
} from 'pages/account/components/account-styles';
import type { IntegrationApp } from '../integration-apps';
import { AppIconTile } from './app-icon-tile';

interface ConnectedAppRowProps {
  app: IntegrationApp;
}

/** App name and status on the left; access, audience, agents and actions on the right (wraps below on phones). */
export function ConnectedAppRow({ app }: ConnectedAppRowProps) {
  const { t } = useTranslation();
  const connection = app.connection;
  if (!connection) return null;

  const needsReconnect = Boolean(connection.needsReconnect);

  return (
    <li className="flex flex-col gap-3 px-4 py-3.5 lg:flex-row lg:items-center lg:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <AppIconTile icon={app.icon} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium leading-body text-grey-950 dark:text-foreground">
              {app.name}
            </p>
            {needsReconnect ? (
              <span
                className={cn(
                  accountBadgeClassName,
                  'bg-warning-background text-warning-high-emphasis'
                )}
              >
                {t('INTEGRATIONS_NEEDS_RECONNECTING', { defaultValue: 'Needs reconnecting' })}
              </span>
            ) : (
              <span className={cn(accountBadgeClassName, 'bg-success-50 text-success-700')}>
                {t('INTEGRATIONS_CONNECTED', { defaultValue: 'Connected' })}
              </span>
            )}
          </div>
          <p className="text-xs leading-heading text-grey-500 dark:text-muted-foreground">
            {app.description}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-grey-600 dark:text-muted-foreground">
        <span>{connection.access}</span>
        <span className="flex items-center gap-1.5">
          <Group width={16} height={16} strokeWidth={1.5} />
          {connection.audience}
        </span>
        <span>
          {t('INTEGRATIONS_AGENT_COUNT', {
            defaultValue: '{{count}} agents',
            count: connection.agentCount,
          })}
        </span>
        <div className="flex w-full gap-2 lg:w-auto">
          {needsReconnect ? (
            <button type="button" className={accountPrimaryButtonClassName}>
              {t('INTEGRATIONS_RECONNECT', { defaultValue: 'Reconnect' })}
            </button>
          ) : null}
          <button type="button" className={accountOutlineButtonClassName}>
            {t('INTEGRATIONS_MANAGE', { defaultValue: 'Manage' })}
          </button>
        </div>
      </div>
    </li>
  );
}
